// /src/components/ProjectSquare.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import GLTFViewer from "../media/GLTFViewer";

const EXPAND_SCALE = 2.5;
const MARGIN_PX = 24;
const PUSH_MAX = 7; // px — max spring displacement on the expanded square

const ProjectSquare = ({ project, isSelected, onClick, onHoverChange, disableNavigation = false, disableExpand = false, forceHovered = false }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const showHover = isHovered || forceHovered;
  const [showExpanded, setShowExpanded] = useState(false);
  const [expandRect, setExpandRect] = useState({ left: 0, top: 0, size: 400 });
  const [cardRect, setCardRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const cardRef = useRef(null);
  const expandedRef = useRef(null);
  const hoverTimer = useRef(null);

  const {
    mediaType, mediaSrc, hoverMediaSrc, hoverMediaType, invert,
    title, description,
    cameraType, cameraPosition, cameraLookAt,
    ambientIntensity, directionalLightPosition, directionalLightTarget,
    directionalLightIntensity, allowPan, targetUrl,
  } = project;

  // Calculate expanded square position in viewport coords (for the portal)
  const calcExpandPosition = useCallback(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const expandedSize = rect.width * EXPAND_SCALE;

    // Store card rect for the collapsed state of the portal
    setCardRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });

    // Center on card in viewport
    let left = rect.left + (rect.width - expandedSize) / 2;
    let top = rect.top + (rect.height - expandedSize) / 2;

    // Clamp to viewport
    if (left < MARGIN_PX) left = MARGIN_PX;
    if (left + expandedSize > window.innerWidth - MARGIN_PX) {
      left = window.innerWidth - MARGIN_PX - expandedSize;
    }
    if (top < MARGIN_PX) top = MARGIN_PX;
    if (top + expandedSize > window.innerHeight - MARGIN_PX) {
      top = window.innerHeight - MARGIN_PX - expandedSize;
    }

    setExpandRect({ left, top, size: expandedSize });
  }, []);

  const handleMouseEnter = () => {
    setIsHovered(true);
    onHoverChange?.(true);
    if (!disableExpand) {
      calcExpandPosition();
      hoverTimer.current = setTimeout(() => {
        setShowExpanded(true);
      }, 120);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowExpanded(false);
    onHoverChange?.(false);
    clearTimeout(hoverTimer.current);
  };


  // Attach a window-level mousemove so we track the cursor even when it's
  // over the expanded portal (which is pointer-events-none in a portal).
  useEffect(() => {
    if (!isHovered) return;

    const onMove = (e) => {
      if (!expandedRef.current || !showExpanded) return;
      const rect = expandedRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const rx = Math.max(-1, Math.min(1, (e.clientX - cx) / (rect.width / 2)));
      const ry = Math.max(-1, Math.min(1, (e.clientY - cy) / (rect.height / 2)));
      gsap.to(expandedRef.current, {
        x: -rx * PUSH_MAX,
        y: -ry * PUSH_MAX,
        duration: 0.4,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [isHovered, showExpanded]);

  // Reset GSAP transform when expanded closes
  useEffect(() => {
    if (!showExpanded && expandedRef.current) {
      gsap.set(expandedRef.current, { x: 0, y: 0 });
    }
  }, [showExpanded]);

  useEffect(() => {
    return () => clearTimeout(hoverTimer.current);
  }, []);

  const handleClick = (e) => {
    if (!disableNavigation && e.button === 0 && targetUrl) navigate(targetUrl);
    if (onClick) onClick(e);
  };

  const handleContextMenu = (e) => e.preventDefault();

  const bgSrc = hoverMediaSrc || mediaSrc;
  const bgType = hoverMediaType || mediaType;

  const renderMedia = (src, type, isForeground = false) => {
    if (!src) return null;
    if (type === "image" || type === "gif") {
      return (
        <img
          src={src}
          alt={title}
          className={`
            w-full h-full transition-transform duration-500 ease-in-out
            ${isForeground ? 'object-contain p-[10%]' : 'object-cover'}
            ${invert ? 'dark:invert-0' : ''}
          `}
        />
      );
    }
    if (type === "3dmodel") {
      return (
        <div className="w-full h-full">
          <GLTFViewer
            src={src}
            cameraType={cameraType} cameraPosition={cameraPosition}
            cameraLookAt={cameraLookAt} ambientIntensity={ambientIntensity}
            directionalLightPosition={directionalLightPosition}
            directionalLightTarget={directionalLightTarget}
            directionalLightIntensity={directionalLightIntensity}
            allowPan={allowPan}
          />
        </div>
      );
    }
    return null;
  };

  // Expanded square rendered as a portal above the blur overlay
  const expandedPortal = ReactDOM.createPortal(
    <div
      ref={expandedRef}
      className={`
        fixed overflow-hidden pointer-events-none will-change-transform
        transition-[left,top,width,height,opacity,box-shadow] duration-400 ease-in-out
        ${showExpanded
          ? 'opacity-100 shadow-[0_20px_60px_rgba(0,0,0,0.22)]'
          : 'opacity-0 shadow-none'
        }
      `}
      style={{
        zIndex: 9991,
        ...(showExpanded
          ? {
              left: `${expandRect.left}px`,
              top: `${expandRect.top}px`,
              width: `${expandRect.size}px`,
              height: `${expandRect.size}px`,
            }
          : {
              left: `${cardRect.left}px`,
              top: `${cardRect.top}px`,
              width: `${cardRect.width}px`,
              height: `${cardRect.height}px`,
            }
        ),
      }}
    >
      {/* Full-bleed image */}
      <div className="absolute inset-0 bg-gray-100 dark:bg-neutral-900">
        {bgSrc && (bgType === "image" || bgType === "gif") && (
          <img
            src={bgSrc}
            alt={title}
            className={`
              w-full h-full object-cover
              transition-transform duration-500 ease-in-out
              ${showExpanded ? 'scale-100' : 'scale-110'}
            `}
          />
        )}
        {bgSrc && bgType === "3dmodel" && renderMedia(bgSrc, bgType, false)}
      </div>

      {/* Gradient for text */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

      {/* Title + description */}
      <div
        className={`
          absolute bottom-0 left-0 right-0
          p-6 lg:p-8
          transition-all duration-350 ease-in-out
          ${showExpanded ? 'opacity-100 translate-y-0 delay-75' : 'opacity-0 translate-y-3'}
        `}
      >
        <h3 className="text-white text-lg lg:text-xl font-normal mb-2 leading-tight">
          {title}
        </h3>
        {description && (
          <p className="text-white/70 text-sm font-light leading-relaxed max-w-[80%]">
            {description}
          </p>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative w-full aspect-square cursor-none clickable"
      style={{ zIndex: isHovered ? 50 : 1 }}
    >
      {/* === BASE CARD === */}
      <div className="absolute inset-0 bg-gray-50 dark:bg-gray-900 overflow-hidden">

        {/* Hover background */}
        <div
          className={`
            absolute inset-0 transition-all duration-300 ease-in-out
            ${showHover ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}
          `}
        >
          {renderMedia(bgSrc, bgType, false)}
        </div>

        {/* Default foreground */}
        <div
          className={`
            absolute inset-0 flex items-center justify-center
            transition-all duration-300 ease-in-out
            ${showHover ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
          `}
        >
          {renderMedia(mediaSrc, mediaType, true)}
        </div>

        {/* Gradient + title */}
        <div
          className={`
            absolute inset-0 
            bg-gradient-to-t from-black/40 via-transparent to-transparent
            transition-opacity duration-300 ease-in-out
            ${showHover ? 'opacity-100' : 'opacity-0'}
          `}
        />
        <div
          className={`
            absolute bottom-0 left-0 right-0 p-4 lg:p-5
            transition-all duration-300 ease-in-out
            ${showHover ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}
          `}
        >
          <h3 className="text-white text-sm font-normal">{title}</h3>
        </div>
      </div>

      {/* Expanded square via portal */}
      {!disableExpand && expandedPortal}
    </div>
  );
};

export default ProjectSquare;
